import { useMemo, useState } from "react";
import { Container } from "../components/ui/Container";
import { Linkedin, Mail, X, User } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/Button";
import { useTranslation } from "react-i18next";

interface TeamMember {
  id: string;
  name: string;
  role: string;
  image: string;
  bio: string;
  linkedin: string;
  email: string;
  group: 'hq' | 'focal';
  experience: Array<{
    company: string;
    role: string;
    period: string;
    description: string;
  }>;
  education: Array<{
    school: string;
    degree: string;
    year: string;
    thesis?: string;
  }>;
  skills: string[];
}

type TeamMemberMeta = Pick<TeamMember, "id" | "name" | "image" | "linkedin" | "email" | "group">;
type TeamMemberI18n = Pick<TeamMember, "id" | "role" | "bio" | "experience" | "education" | "skills">;

const teamMembersMeta: TeamMemberMeta[] = [
  {
    id: "1",
    name: "Jacques NDAVARO",
    image:
      "https://res.cloudinary.com/daxvxdecv/image/upload/v1743090202/kiota_suit/Team/a9p17zju63rrht1sf9nl.jpg",
    linkedin: "https://www.linkedin.com/in/jacques-ndavaro-b02b25111",
    email: "jacquesndav@ikiotahub.com",
    group: "hq",
  },
  {
    id: "2",
    name: "NEKA MBASA PhD",
    image:
      "https://res.cloudinary.com/daxvxdecv/image/upload/v1743091820/kiota_suit/Team/n0zri3f1jxurxcev0afc.jpg",
    linkedin: "https://linkedin.com/in/jp-mugisha",
    email: "Neka.Mbasa@ikiotahub.com",
    group: "hq",
  },
  {
    id: "3",
    name: "Victoire KASEREKA",
    image:
      "https://res.cloudinary.com/daxvxdecv/image/upload/v1743086266/kiota_suit/Team/nwy7nosyu5zww8y6g02m.jpg",
    linkedin: "https://www.linkedin.com/in/victoire-kasereka-33603b20b/",
    email: "Victoire.Kasereka@ikiotahub.com",
    group: "hq",
  },
  {
    id: "4",
    name: "Anthony RUBONEKA",
    image:
      "https://res.cloudinary.com/daxvxdecv/image/upload/v1742916817/kiota_suit/Team/k3iq7ynuuxebkcl92o6t.jpg",
    linkedin:
      "https://www.linkedin.com/in/anthony-wany-ab1a921bb?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
    email: "anthonyrub@ikiotahub.com",
    group: "hq",
  },
  {
    id: "5",
    name: "Kelvine KAHINDO",
    image: "https://res.cloudinary.com/daxvxdecv/image/upload/v1743925556/IMG_6731_hkw58h.jpg",
    linkedin: "https://www.linkedin.com/in/st%C3%A9phanie-kahindo-kelvine-a34847186/",
    email: "kelvinesteph@ikiotahub.com",
    group: "focal",
  },
  {
    id: "6",
    name: "Joseph KANIKI",
    image:
      "https://res.cloudinary.com/daxvxdecv/image/upload/v1743091820/kiota_suit/Team/placeholder.jpg",
    linkedin: "",
    email: "joseph.kaniki@ikiotahub.com",
    group: "hq",
  },
  {
    id: "7",
    name: "MBAMBU SYAYIGHANZA Edwige, PhD",
    image:
      "https://res.cloudinary.com/daxvxdecv/image/upload/v1743091820/kiota_suit/Team/placeholder.jpg",
    linkedin: "",
    email: "",
    group: "hq",
  },
  {
    id: "8",
    name: "Junior",
    image:
      "https://res.cloudinary.com/daxvxdecv/image/upload/v1774879569/JuniorK_vfx9he.png",
    linkedin: "",
    email: "junior@ikiotahub.com",
    group: "focal",
  },
];

export function Team() {
  const { t, i18n } = useTranslation();
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const teamMembers = useMemo(() => {
    const localizedMembers = t("team.members", { returnObjects: true }) as unknown as TeamMemberI18n[];
    const byId = new Map((localizedMembers || []).map((m) => [m.id, m]));

    return teamMembersMeta.map((meta) => {
      const localized = byId.get(meta.id);
      return {
        ...meta,
        role: localized?.role ?? "",
        bio: localized?.bio ?? "",
        experience: localized?.experience ?? [],
        education: localized?.education ?? [],
        skills: localized?.skills ?? [],
      };
    });
  }, [i18n.language, t]);

  const hqMembers = teamMembers.filter(m => m.group === 'hq');
  const focalMembers = teamMembers.filter(m => m.group === 'focal');

  const renderMemberCard = (member: TeamMember) => (
    <motion.div
      key={member.id}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="relative group max-w-xs mx-auto w-full"
    >
      <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-gray-100 relative">
        <img
          src={member.image}
          alt={member.name}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent && !parent.querySelector('.avatar-fallback')) {
              const fallback = document.createElement('div');
              fallback.className = 'avatar-fallback absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/10';
              const iconContainer = document.createElement('div');
              iconContainer.className = 'text-primary/40';
              const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
              svg.setAttribute('width', '96');
              svg.setAttribute('height', '96');
              svg.setAttribute('viewBox', '0 0 24 24');
              svg.setAttribute('fill', 'none');
              svg.setAttribute('stroke', 'currentColor');
              svg.setAttribute('stroke-width', '1.5');
              svg.setAttribute('stroke-linecap', 'round');
              svg.setAttribute('stroke-linejoin', 'round');
              const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
              path.setAttribute('d', 'M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2');
              const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
              circle.setAttribute('cx', '12');
              circle.setAttribute('cy', '7');
              circle.setAttribute('r', '4');
              svg.appendChild(path);
              svg.appendChild(circle);
              iconContainer.appendChild(svg);
              fallback.appendChild(iconContainer);
              parent.insertBefore(fallback, parent.firstChild);
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
        <h3 className="text-xl font-semibold">{member.name}</h3>
        <p className="mt-1 text-gray-300">{member.role}</p>

        <div className="mt-4 flex gap-3">
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <Linkedin className="h-5 w-5" />
          </a>
          <a
            href={`mailto:${member.email}`}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <Mail className="h-5 w-5" />
          </a>
          <button
            onClick={() => setSelectedMember(member)}
            className="text-sm bg-white/10 px-4 py-2 rounded-full hover:bg-white/20 transition-colors"
          >
            {t('common.seeProfile')}
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="relative overflow-hidden min-h-screen" style={{ background: 'linear-gradient(180deg, #f3f4f6 0%, #e5e7eb 100%)' }}>
      {/* SVG curves for fluidity and section connection */}
      <svg className="absolute top-0 left-0 w-full h-32 z-0" viewBox="0 0 1440 160" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <path d="M0,80 C360,160 1080,0 1440,80 L1440,0 L0,0 Z" fill="#e5e7eb" />
      </svg>
      <svg className="absolute top-32 left-0 w-full h-24 z-0" viewBox="0 0 1440 96" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <path d="M0,48 C480,96 960,0 1440,48 L1440,0 L0,0 Z" fill="#f3f4f6" />
      </svg>
      <Container className="relative z-10 py-12 lg:py-20">
        {/* En-tête */}
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            {t('team.title')}
          </h1>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            {t('team.subtitle')}
          </p>
        </div>

        {/* Section Équipe HQ — Goma */}
        <section className="mt-16">
          <div className="flex items-center gap-4 mb-10">
            <span className="inline-flex items-center rounded-full bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary ring-1 ring-inset ring-primary/20">
              {t('team.hqSection')}
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {hqMembers.map(renderMemberCard)}
          </div>
        </section>

        {/* Section Points Focaux & Représentants */}
        <section className="mt-20">
          <div className="flex items-center gap-4 mb-10">
            <span className="inline-flex items-center rounded-full bg-teal-50 px-4 py-1.5 text-sm font-semibold text-teal-700 ring-1 ring-inset ring-teal-600/20">
              {t('team.focalSection')}
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {focalMembers.map(renderMemberCard)}
          </div>
        </section>

        {/* Modal de détails */}
        {selectedMember && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

              <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                <div className="sticky top-0 right-0 z-10 flex justify-end pr-3 pt-3 bg-white">
                  <button
                    onClick={() => setSelectedMember(null)}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="bg-white px-4 pb-4 sm:px-6">
                  <div className="sm:flex sm:items-start">
                    <div className="text-center sm:mt-0 sm:text-left w-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="relative h-14 w-14 rounded-full overflow-hidden bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0">
                          <img
                            src={selectedMember.image}
                            alt={selectedMember.name}
                            className="h-14 w-14 rounded-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                          <User className="h-7 w-7 text-primary/40 absolute" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {selectedMember.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {selectedMember.role}
                          </p>
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 mb-6">
                        {selectedMember.bio}
                      </p>

                      <div className="space-y-6">
                        {/* Expérience */}
                        <div>
                          <h4 className="text-base font-semibold text-gray-900 mb-3">
                            {t('team.experience')}
                          </h4>
                          <div className="space-y-3">
                            {selectedMember.experience.map((exp, index) => (
                              <div
                                key={index}
                                className="border-l-2 border-primary pl-3"
                              >
                                <h5 className="text-sm font-medium text-gray-900">
                                  {exp.company}
                                </h5>
                                <p className="text-xs text-gray-600">
                                  {exp.role}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {exp.period}
                                </p>
                                <p className="mt-1 text-xs text-gray-600">
                                  {exp.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Formation */}
                        <div>
                          <h4 className="text-base font-semibold text-gray-900 mb-3">
                            {t('team.education')}
                          </h4>
                          <div className="space-y-3">
                            {selectedMember.education.map((edu, index) => (
                              <div key={index}>
                                <h5 className="text-sm font-medium text-gray-900">
                                  {edu.school}
                                </h5>
                                <p className="text-xs text-gray-600">
                                  {edu.degree}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {edu.year}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Compétences */}
                        <div>
                          <h4 className="text-base font-semibold text-gray-900 mb-3">
                            {t('team.skills')}
                          </h4>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedMember.skills.map((skill, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <Button onClick={() => setSelectedMember(null)}>
                    {t('common.close')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}
